/* CommandPalette — global Cmd+K / Ctrl+K shortcut.
 *
 * Press ⌘K (mac) or Ctrl+K (windows/linux) anywhere in the app to open a
 * Spotlight-style command palette with role-aware quick actions.
 *
 * Admin (Feb 2026): Includes every route from ADMIN_GROUPS in PortalShell —
 *  34 routes grouped by Risk & Credit / Commerce / Money / Security & Audit
 *  / Partners / Governance / Support / System. Grouped section headers
 *  rendered in the palette list (Stripe-Dashboard / Linear pattern).
 *
 * Triggers:
 *   • Global keydown: ⌘K / Ctrl+K
 *   • Window event "open-command-palette" (fired by the top-nav pill)
 *
 * Accessible: focus trap, ESC to close, ↑/↓ navigation, ENTER to run.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search, ArrowRight, Bug, Lightbulb, Mail, LayoutDashboard,
  Package, Users, Wallet, ShieldCheck, ShoppingBag, MessageSquare,
  Settings, FileText, BarChart3, Truck, Receipt, Compass,
  Sparkles, Map as MapIcon, Shield, TrendingUp, FileSearch, Activity, CreditCard,
  Eye, Store, AlertTriangle, ShieldAlert, DollarSign, Gauge,
  Building2, Plug, Webhook, ExternalLink, Gavel, Settings2, Globe,
  HeartPulse, Megaphone, LayoutGrid, Banknote, ShoppingCart,
} from "lucide-react";

const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

// Admin navigation catalog — mirrors ADMIN_GROUPS in PortalShell.jsx.
// Each entry: { group, label, to, icon, keywords }.
const ADMIN_NAV = [
  { group: "Platform", label: "Platform overview", to: "/admin", icon: LayoutDashboard, keywords: ["home", "dashboard", "overview"] },

  { group: "Risk & Credit", label: "Buyers · Scoring", to: "/admin/buyers", icon: TrendingUp, keywords: ["buyers", "scoring", "users", "credit", "score"] },
  { group: "Risk & Credit", label: "Underwriting Hub", to: "/admin/underwriting", icon: FileSearch, keywords: ["underwriting", "risk", "kyb"] },
  { group: "Risk & Credit", label: "AI Risk Engine", to: "/admin/risk-engine", icon: Activity, keywords: ["risk", "ai", "engine", "fraud"] },
  { group: "Risk & Credit", label: "Credit Approvals", to: "/admin/credit", icon: CreditCard, keywords: ["credit", "approvals", "limits"] },
  { group: "Risk & Credit", label: "Insurance", to: "/admin/insurance", icon: Shield, keywords: ["insurance", "allianz", "cover"] },
  { group: "Risk & Credit", label: "Coverage Console", to: "/admin/coverage", icon: Eye, keywords: ["coverage", "cover", "allianz"] },

  { group: "Commerce", label: "Orders & Commerce", to: "/admin/commerce", icon: Store, keywords: ["orders", "commerce", "invoices"] },
  { group: "Commerce", label: "Catalog Oversight", to: "/admin/catalog", icon: LayoutGrid, keywords: ["catalog", "products", "skus", "oversight"] },
  { group: "Commerce", label: "QI Claims", to: "/admin/claims", icon: AlertTriangle, keywords: ["claims", "qi", "quality", "disputes"] },
  { group: "Commerce", label: "Dropship Ops", to: "/admin/dropship", icon: Package, keywords: ["dropship", "shopify", "fulfillment"] },
  { group: "Commerce", label: "Shipping Console", to: "/admin/shipping", icon: Truck, keywords: ["shipping", "shippo", "tracking", "freight"] },

  { group: "Money", label: "Wallets", to: "/admin/wallets", icon: Wallet, keywords: ["wallets", "balance", "ledger"] },
  { group: "Money", label: "Money Mission Control", to: "/admin/payments-ops", icon: ShieldAlert, keywords: ["payments", "ops", "tabapay", "money", "mission"] },
  { group: "Money", label: "verqo Revenue", to: "/admin/revenue", icon: TrendingUp, keywords: ["revenue", "mrr", "growth"] },
  { group: "Money", label: "Platform Fees", to: "/admin/platform-fees", icon: DollarSign, keywords: ["fees", "platform", "rake"] },
  { group: "Money", label: "Refund Quotas", to: "/admin/quotas", icon: Gauge, keywords: ["refund", "quotas", "limits"] },
  { group: "Money", label: "Finance (Treasury)", to: "/admin/finance", icon: DollarSign, keywords: ["finance", "treasury", "cash", "portfolio"] },
  { group: "Money", label: "Finance Hub", to: "/admin/finance-hub", icon: Banknote, keywords: ["reconciliation", "recon", "dunning", "disputes", "chargeback", "subscriptions", "saas", "payouts", "1099", "tax"] },

  { group: "Security & Audit", label: "Stuck Operations", to: "/admin/stuck-ops", icon: AlertTriangle, keywords: ["stuck", "ops", "force", "advance", "rescue", "signup", "plaid", "warp", "shippo", "credit", "insurance", "payments"] },
  { group: "Security & Audit", label: "Security Center", to: "/admin/security", icon: Shield, keywords: ["security", "sso", "rbac", "break glass", "elevations"] },
  { group: "Security & Audit", label: "Override Audit", to: "/admin/overrides", icon: ShieldCheck, keywords: ["override", "audit", "trail"] },
  { group: "Security & Audit", label: "Activity Log", to: "/admin/activity", icon: Activity, keywords: ["activity", "log", "audit"] },
  { group: "Security & Audit", label: "User Timeline", to: "/admin/timeline", icon: Activity, keywords: ["timeline", "user", "history"] },

  { group: "Partners & Integrations", label: "Partner Orgs", to: "/admin/partner-orgs", icon: Building2, keywords: ["partners", "orgs", "lender", "carrier", "banking"] },
  { group: "Partners & Integrations", label: "Integrations", to: "/admin/integrations", icon: Plug, keywords: ["integrations", "providers", "keys", "plaid", "stripe"] },
  { group: "Partners & Integrations", label: "Webhook Explorer", to: "/admin/webhooks", icon: Webhook, keywords: ["webhooks", "events", "explorer", "shippo", "two.inc", "allianz"] },
  { group: "Partners & Integrations", label: "Portal Access", to: "/admin/portal-access", icon: ExternalLink, keywords: ["portal", "access", "links", "partner"] },

  { group: "Governance", label: "Compliance", to: "/admin/compliance", icon: ShieldCheck, keywords: ["compliance", "kyc", "kyb"] },
  { group: "Governance", label: "Legal Queue", to: "/admin/legal", icon: Gavel, keywords: ["legal", "queue", "disputes"] },
  { group: "Governance", label: "Operations", to: "/admin/operations", icon: Settings2, keywords: ["operations", "crons", "ops", "schedules"] },

  { group: "Support", label: "Tickets", to: "/admin/support", icon: MessageSquare, keywords: ["support", "tickets", "inbox"] },
  { group: "Support", label: "Support Console", to: "/admin/support-console", icon: Sparkles, keywords: ["support", "console", "ai"] },

  { group: "System", label: "All Users", to: "/admin/users", icon: Users, keywords: ["users", "members", "directory"] },
  { group: "System", label: "Domains", to: "/admin/domains", icon: Globe, keywords: ["domains", "dns", "cloudflare", "custom"] },
  { group: "System", label: "Platform Health", to: "/admin/platform-health", icon: HeartPulse, keywords: ["feature flags", "maintenance", "db health", "tos", "privacy", "queues", "platform"] },
  { group: "System", label: "Communications", to: "/admin/comms", icon: Megaphone, keywords: ["email", "sms", "templates", "announcements", "broadcast", "resend", "twilio"] },
  { group: "System", label: "Admin Hub", to: "/admin/mgmt-hub", icon: LayoutGrid, keywords: ["api keys", "login attempts", "dsr", "gdpr", "documents", "outbound webhooks", "knowledge base", "cohorts", "affiliate", "referral", "returns", "rma"] },
  { group: "System", label: "Admin Settings", to: "/admin/settings", icon: Shield, keywords: ["settings", "admin", "config"] },
];

// Universal commands (every authenticated user).
function universalCommands({ help }) {
  return [
    { group: "Help",   id: "report-bug",       label: "Report a bug",       hint: "Opens a bug report",         icon: Bug,        action: help("bug"),                                  keywords: ["bug", "broken", "issue", "error", "report", "problem"] },
    { group: "Help",   id: "request-feature",  label: "Request a feature",  hint: "Suggest something new",      icon: Lightbulb,  action: help("feature"),                              keywords: ["feature", "request", "idea", "suggestion", "improvement"] },
    { group: "Help",   id: "view-roadmap",     label: "View the roadmap",   hint: "Vote on what we build next", icon: MapIcon,    action: ({ navigate }) => navigate("/roadmap"),       keywords: ["roadmap", "vote", "upcoming", "shipping", "planned", "future"] },
    { group: "Help",   id: "email-support",    label: "Email support",      hint: "support@verqo.com",          icon: Mail,       action: () => { window.location.href = "mailto:support@verqo.com"; }, keywords: ["email", "support", "contact", "help"] },
    { group: "Help",   id: "ask-question",     label: "Ask a question",     hint: "Opens a support ticket",     icon: MessageSquare, action: help("question"),                          keywords: ["question", "ask", "help", "support"] },
  ];
}

// Role-aware command catalog. Each command has:
//   id, group, label, hint, icon, action() → fires nav or event
function buildCommands({ role, navigate, openHelp }) {
  const nav = (path) => () => navigate(path);
  const help = (type, prefill = {}) => () => openHelp(type, prefill);

  // Universal commands rendered last so navigation surfaces first.
  const universal = universalCommands({ help }).map((c) => ({
    ...c,
    action: typeof c.action === "function" ? (c.action.length ? () => c.action({ navigate }) : c.action) : c.action,
  }));

  let navigation = [];
  if (role === "buyer") {
    navigation = [
      { id: "go-buyer-dashboard", group: "Navigate", label: "Dashboard",  icon: LayoutDashboard, action: nav("/buyer"),          keywords: ["home", "dashboard", "overview"] },
      { id: "go-buyer-orders",    group: "Navigate", label: "Orders",     icon: ShoppingCart,         action: nav("/buyer/orders"),   keywords: ["orders", "po", "purchase"] },
      { id: "go-buyer-cart",      group: "Navigate", label: "Cart",       icon: ShoppingBag,     action: nav("/buyer/cart"),     keywords: ["cart", "checkout"] },
      { id: "go-buyer-payments",  group: "Navigate", label: "Payments",   icon: Wallet,          action: nav("/buyer/payments"), keywords: ["payments", "wallet", "credit", "pay"] },
      { id: "go-buyer-shipping",  group: "Navigate", label: "Shipping",   icon: Truck,           action: nav("/buyer/shipping"), keywords: ["shipping", "tracking", "freight"] },
      { id: "go-buyer-score",     group: "Navigate", label: "Verqo Trade Score", icon: BarChart3,      action: nav("/buyer/score"),    keywords: ["score", "trade", "credit", "rating"] },
      { id: "go-buyer-suppliers", group: "Navigate", label: "Suppliers",  icon: Users,           action: nav("/buyer/suppliers"), keywords: ["suppliers", "sellers", "vendors"] },
      { id: "go-buyer-settings",  group: "Navigate", label: "Settings",   icon: Settings,        action: nav("/buyer/settings"), keywords: ["settings", "profile", "preferences"] },
    ];
  } else if (role === "seller") {
    navigation = [
      { id: "go-seller-dashboard", group: "Navigate", label: "Dashboard", icon: LayoutDashboard, action: nav("/seller"),          keywords: ["home", "dashboard", "overview"] },
      { id: "go-seller-orders",    group: "Navigate", label: "Orders",    icon: ShoppingCart,         action: nav("/seller/orders"),   keywords: ["orders", "fulfillment"] },
      { id: "go-seller-buyers",    group: "Navigate", label: "Buyers",    icon: Users,           action: nav("/seller/buyers"),   keywords: ["buyers", "customers", "retailers"] },
      { id: "go-seller-catalog",   group: "Navigate", label: "Catalog",   icon: ShoppingBag,     action: nav("/seller/catalog"),  keywords: ["products", "catalog", "skus"] },
      { id: "go-seller-wallet",    group: "Navigate", label: "Wallet",    icon: Wallet,          action: nav("/seller/wallet"),   keywords: ["wallet", "payouts", "balance"] },
      { id: "go-seller-shipping",  group: "Navigate", label: "Shipping",  icon: Truck,           action: nav("/seller/shipping"), keywords: ["shipping", "labels", "freight"] },
      { id: "go-seller-insurance", group: "Navigate", label: "Insurance", icon: ShieldCheck,     action: nav("/seller/insurance"), keywords: ["insurance", "allianz", "cover"] },
      { id: "go-seller-settings",  group: "Navigate", label: "Settings",  icon: Settings,        action: nav("/seller/settings"), keywords: ["settings", "profile"] },
    ];
  } else if (role === "admin") {
    navigation = ADMIN_NAV.map((n, i) => ({
      id: `go-admin-${n.to.replace(/[^a-z0-9]+/gi, "-")}-${i}`,
      group: n.group,
      label: n.label,
      icon: n.icon || ArrowRight,
      action: nav(n.to),
      keywords: n.keywords,
      hint: n.to,
    }));
  }

  return [...navigation, ...universal];
}


export default function CommandPalette({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const openHelpEvent = (type, prefill = {}) => {
    window.dispatchEvent(new CustomEvent("open-help-ticket", {
      detail: { type, ...prefill },
    }));
  };

  // Build commands once per user role.
  const commands = useMemo(
    () => buildCommands({ role: user?.role, navigate, openHelp: openHelpEvent }),
    [user?.role, navigate],
  );

  // Filter by search query (label + group + keywords).
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return commands;
    return commands.filter((c) => {
      if (c.label.toLowerCase().includes(needle)) return true;
      if ((c.group || "").toLowerCase().includes(needle)) return true;
      return (c.keywords || []).some((k) => k.toLowerCase().includes(needle));
    });
  }, [commands, q]);

  // Group flat list into { group: [items] } preserving original order.
  const grouped = useMemo(() => {
    const out = [];
    const seen = new Map();
    for (const c of filtered) {
      const g = c.group || "Other";
      if (!seen.has(g)) {
        seen.set(g, out.length);
        out.push({ group: g, items: [] });
      }
      out[seen.get(g)].items.push(c);
    }
    return out;
  }, [filtered]);

  // Reset selection when filter changes.
  useEffect(() => { setActive(0); }, [q]);

  // Close on route change (palette is meant to be transient).
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Auto-scroll active row into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${active}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [active, open]);

  // Global Cmd+K / Ctrl+K binding + escape + arrow navigation.
  useEffect(() => {
    const onKey = (e) => {
      const cmdK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (cmdK) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (!open) return;
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const cmd = filtered[active];
        if (cmd) {
          cmd.action();
          setOpen(false);
          setQ("");
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, filtered, active]);

  // External open trigger (used by the top-nav search pill).
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-command-palette", onOpen);
    return () => window.removeEventListener("open-command-palette", onOpen);
  }, []);

  // Auto-focus search input on open.
  useEffect(() => {
    if (open && inputRef.current) {
      // Defer to allow render
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ("");
      setActive(0);
    }
  }, [open]);

  if (!open) return null;

  // Build a flat-index lookup so grouped rendering can still reference
  // the active row by its position in `filtered`.
  let flatIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4 bg-foreground/30 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      data-testid="command-palette-overlay"
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-border bg-white shadow-2xl overflow-hidden"
        data-testid="command-palette"
        role="dialog"
        aria-modal="true"
      >
        {/* Search bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-foreground/40 shrink-0"/>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={user?.role === "admin" ? "Jump to any admin page or action…" : "Type a command or search…"}
            data-testid="command-palette-input"
            className="flex-1 text-[14px] bg-transparent focus:outline-none placeholder:text-foreground/40"
          />
          <kbd className="text-[10px] mono px-1.5 py-0.5 rounded border border-border text-foreground/55">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto" ref={listRef}>
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-foreground/55" data-testid="command-palette-empty">
              <Sparkles className="w-5 h-5 mx-auto mb-2 text-foreground/30"/>
              No matches. Try "<span className="mono">refund</span>", "<span className="mono">webhooks</span>" or "<span className="mono">security</span>".
            </div>
          ) : (
            <div className="py-2">
              {grouped.map((g) => (
                <div key={g.group} data-testid={`cmd-group-${g.group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-widest text-foreground/45 mono">
                    {g.group}
                  </div>
                  <ul>
                    {g.items.map((c) => {
                      flatIdx += 1;
                      const idx = flatIdx;
                      const Icon = c.icon || ArrowRight;
                      const isActive = idx === active;
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            data-cmd-idx={idx}
                            data-testid={`cmd-${c.id}`}
                            onMouseEnter={() => setActive(idx)}
                            onClick={() => { c.action(); setOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-left transition-colors ${isActive ? "bg-secondary/70" : "hover:bg-secondary/40"}`}
                          >
                            <Icon className="w-4 h-4 shrink-0 text-foreground/55"/>
                            <span className="flex-1 truncate">{c.label}</span>
                            {c.hint && (
                              <span className="text-[11px] text-foreground/45 truncate hidden sm:inline mono">
                                {c.hint}
                              </span>
                            )}
                            {isActive && (
                              <ArrowRight className="w-3.5 h-3.5 text-foreground/55 shrink-0"/>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border flex items-center justify-between text-[10.5px] text-foreground/55 bg-secondary/30">
          <div className="flex items-center gap-2">
            <kbd className="mono px-1 py-0.5 rounded border border-border">↑</kbd>
            <kbd className="mono px-1 py-0.5 rounded border border-border">↓</kbd>
            <span>navigate</span>
            <kbd className="mono px-1 py-0.5 rounded border border-border ml-2">↵</kbd>
            <span>run</span>
          </div>
          <div className="mono">{isMac ? "⌘K" : "Ctrl+K"}</div>
        </div>
      </div>
    </div>
  );
}
